import { useState, useEffect, useCallback, useMemo } from 'react';
import { LOCAL_STORAGE_KEYS } from 'utils/fuctions/localStorage';
import { loadObjectFromLocalStorage } from 'utils/fuctions/localStorage';
import QueueService from 'views/Queue/service/index.js';
import * as y from 'yup';
import { toast } from 'react-toastify';

export const QueueDistributionStatus = {
  ASSIGN: 'ASSIGN',
  IN_SERVICE: 'IN_SERVICE'
};

/**
 * @typedef {Object} CentroDeCusto
 * @property {number} id
 * @property {string} name
 * @property {string} description
 */
const CentroDeCustoSchema = y
  .object({
    id: y.number().required(),
    name: y.string().required(),
    description: y.string().required()
  })
  .required();

export const QueueSchema = y.object({
  id: y.number().required(),
  timeout: y.number().required(),
  musiconhold: y.string().nullable().defined(),
  wrapuptime: y.number().required(),
  autopause: y.boolean().required(),
  ringinuse: y.boolean().required(),
  strategy: y.string().required(),
  name: y.string().required(),
  periodic_announce: y.string().nullable().defined(),
  periodic_announce_frequency: y.number().required(),
  fila_personalizada: y.boolean().required(),
  foraHorario: y.string().nullable().defined(),
  disable: y.boolean().required(),

  closing_message: y.string().default('').nullable(),
  closing_message_enabled: y.boolean().required(),
  greeting_message: y.string().default('').nullable(),
  greeting_message_enabled: y.boolean().required(),
  transfer_message: y.string().default('').nullable(),
  transfer_message_enabled: y.boolean().required(),
  send_attendant_name: y.boolean().required(),
  strategy_chat: y.string().required(),
  max_chat_per_user: y.number().required(),
  chat_overflow: y.string().nullable().defined(),
  queue_of_chat_overflow: y.string().nullable().defined(),
  message_of_chat_overflow: y.string().default('').nullable(),
  active_chat_in_chat_distribution: y.boolean().required(),
  maximum_queue_time: y.number().nullable().defined(),
  check_maximum_queue_time: y.boolean().required(),
  scheduleTime_id: y.number().required(),
  centrodecustoId: y.number().required(),
  callback_id: y.number().nullable().defined(),
  color: y.string().required(),
  is_chat: y.boolean().required(),
  is_telephony: y.boolean().required(),
  levelOfService: y.number().required(),
  centrodecusto: CentroDeCustoSchema,
  distribution_status: y
    .string()
    .oneOf(Object.values(QueueDistributionStatus))
    .required(),
  distribution_auto_message: y.string().nullable()
});

/**
 * @typedef {Object} AgentQueue
 * @property {number} queueId
 * @property {string} queueName
 */
const AgentQueueSchema = y.object({
  queueId: y.number().required(),
  queueName: y.string().required()
});

/**
 * @typedef {Object} AgentAllowedQueues
 * @property {string} agentLogin
 * @property {string} agentName
 * @property {AgentQueue[]} queues
 */
const AgentAllowedQueuesSchema = y.object({
  agentLogin: y.string().required(),
  agentName: y.string().required(),
  queues: y.array().of(AgentQueueSchema).required()
});

/**
 * Hook para buscar todas as filas existentes, de chat e de telefonia.
 * É possível escolher entre mostrar ou esconder as filas desabilitadas e entre quais filas receber (chat, telefonia ou ambas).
 * Por padrão, retorna todos os tipos de fila.
 * @param {Object} [props={}]
 * @param {boolean} [props.showDisabled=false]
 * @param {("is_chat" |  "is_telephony")[]} [props.types=["is_chat", "is_telephony"]] - quais filas devem ser retornadas (escrever o nome da propriedade)
 * @param {boolean} [props.shouldUseOnlyAllowedUserQueues=false] - Indica se devem ser levadas em consideração apenas as filas às quais o agente tem permissão
 * @param {{key: keyof QueueSchema, order: 'asc' | 'desc'}} [props.sort=null] - Parâmetro opcional para ordenação.
 * @returns
 */
const useQueues = (props = {}) => {
  const {
    showDisabled = false,
    types = ['is_chat', 'is_telephony'],
    shouldUseOnlyAllowedUserQueues = false,
    sort = null,
    manual = false
  } = props;

  const [state, setState] = useState({
    /** @type {y.ObjectSchema<QueueSchema>[]} */
    response: [],
    isLoading: false,
    loginToAllowedQueuesMap: {},
    loginToAllowedQueuesOptionsMap: {},
    isError: false,
    error: null
  });

  /**
   * @type {{id: number, name: string}[]} As filas que o usuário possui permissão
   */
  const allowedUserQueues =
    loadObjectFromLocalStorage(LOCAL_STORAGE_KEYS.USER_ALLOWED_QUEUES) ?? [];

  // Filas em formato de FlexControllerSelectable
  const options = useMemo(
    () => state.response.map(({ id, name }) => ({ label: name, value: id })),
    [state.response]
  );

  // Filas agrupadas por telefonia e por chat
  const groupedByType = useMemo(() => {
    return state.response.reduce(
      (prev, curr) => {
        prev[curr.is_chat ? 'chat' : 'telephony'].push(curr);
        return prev;
      },
      {
        chat: [],
        telephony: []
      }
    );
  }, [state.response]);

  const idMap = useMemo(
    () =>
      state.response.reduce((map, queue) => {
        map.set(queue.id, queue);
        return map;
      }, new Map()),
    [state.response]
  );

  const fetch = useCallback(async () => {
    setState((prevState) => ({
      ...prevState,
      isLoading: true,
      isError: false,
      error: null
    }));

    try {
      const queuesPromise = showDisabled
        ? QueueService.getAllShowDisable(true)
        : QueueService.getAll();

      const [queuesResult, agentsResult] = await Promise.allSettled([
        queuesPromise,
        QueueService.getAgentsAllowedQueues()
      ]);

      let filteredData = [];
      let loginToAllowedQueuesMap = {};
      let loginToAllowedQueuesOptionsMap = {};
      let errorOccurred = null;

      if (queuesResult.status === 'fulfilled') {
        const rawQueuesData = queuesResult.value?.data?.data ?? [];

        const queuesValidation = y
          .array()
          .of(QueueSchema)
          .safeValidateSync(rawQueuesData);

        if (!queuesValidation.success) {
          throw new Error(
            queuesValidation.error.message || 'Erro de validação nas filas'
          );
        }

        const queuesData = queuesValidation.data;

        filteredData = queuesData.filter((queue) =>
          types.some((type) => queue[type])
        );

        if (shouldUseOnlyAllowedUserQueues) {
          filteredData = filteredData.filter((queue) =>
            allowedUserQueues.some((q) => q.id === queue.id)
          );
        }

        if (sort && sort.key) {
          filteredData.sort((a, b) => {
            const valA = a[sort.key];
            const valB = b[sort.key];
            const direction = sort.order === 'desc' ? -1 : 1;

            if (typeof valA === 'string') {
              return valA.localeCompare(valB) * direction;
            }
            if (valA < valB) return -1 * direction;
            if (valA > valB) return 1 * direction;
            return 0;
          });
        }
      } else {
        console.error('Erro ao buscar filas:', queuesResult.reason);
        errorOccurred = queuesResult.reason;
        toast.error('Não foi possível carregar a lista de filas.');
      }

      if (agentsResult.status === 'fulfilled') {
        const rawAgentsData = agentsResult.value?.data?.data ?? [];

        const agentsValidation = y
          .array()
          .of(AgentAllowedQueuesSchema)
          .safeValidateSync(rawAgentsData);

        if (!agentsValidation.success) {
          throw new Error(
            agentsValidation.error.message || 'Erro de validação nos agentes'
          );
        }

        const agentsData = agentsValidation.data;

        loginToAllowedQueuesMap = Object.fromEntries(
          agentsData.map((res) => [res.agentLogin, res])
        );

        loginToAllowedQueuesOptionsMap = Object.fromEntries(
          agentsData.map((agent) => [
            agent.agentLogin,
            agent.queues.map((queue) => ({
              label: queue.queueName,
              value: queue.queueId
            }))
          ])
        );
      } else {
        console.error('Erro ao buscar agentes:', agentsResult.reason);

        if (!errorOccurred) {
          errorOccurred = agentsResult.reason;
        }
      }

      setState((prevState) => ({
        ...prevState,
        response: filteredData,
        isLoading: false,
        loginToAllowedQueuesMap,
        loginToAllowedQueuesOptionsMap,
        isError: !!errorOccurred,
        error: errorOccurred
      }));
    } catch (err) {
      setState((prevState) => ({
        ...prevState,
        response: [],
        loginToAllowedQueuesMap: {},
        loginToAllowedQueuesOptionsMap: {},
        isError: true,
        error: err,
        isLoading: false
      }));
    }
  }, [
    JSON.stringify(types),
    shouldUseOnlyAllowedUserQueues,
    JSON.stringify(allowedUserQueues),
    JSON.stringify(sort),
    showDisabled
  ]);

  /**
   * Executa o logout de um agente específico em uma ou mais filas.
   * @param {Object} logoutProps
   * @param {string} logoutProps.login - O login do agente a ser deslogado.
   * @param {number[]} logoutProps.queuesToLogout - Array com os IDs das filas.
   */
  const logoutByAgentLoginAndQueueIds = useCallback(async (logoutProps) => {
    try {
      const response = await QueueService.logoutByAgentLoginAndQueueIds(
        logoutProps
      );
      return response;
    } catch (error) {
      toast.error(
        error?.response?.data?.message ??
          'Houve um erro inesperado durante o logout nas filas'
      );
      throw new Error(
        error?.response?.data?.message ??
          'Houve um erro inesperado durante o logout nas filas'
      );
    }
  }, []);

  /**
   * Executa o login de um agente específico em uma ou mais filas.
   * @param {Object} loginProps
   * @param {string} loginProps.login - O login do agente a ser deslogado.
   * @param {number[]} loginProps.queuesToLogin - Array com os IDs das filas.
   */
  const loginByAgentLoginAndQueueIds = useCallback(async (loginProps) => {
    const response = await QueueService.loginByAgentLoginAndQueueIds(
      loginProps
    );

    if (response?.isError) {
      toast.info(
        response?.message ??
          'Houve um erro inesperado durante o login nas filas'
      );
      throw new Error(
        response?.message ??
          'Houve um erro inesperado durante o login nas filas'
      );
    }

    return response;
  }, []);

  useEffect(() => {
    if (!manual) {
      fetch();
    }
  }, [fetch, showDisabled]);

  return {
    ...state,
    options,
    groupedByType,
    idMap,
    fetch,
    logoutByAgentLoginAndQueueIds,
    loginByAgentLoginAndQueueIds
  };
};

export default useQueues;
